import { 
  ANALYTICS_EVENTS, 
  ANALYTICS_CATEGORIES, 
  EVENT_PARAMETERS,
  CUSTOM_DIMENSIONS,
  AB_TEST_CONFIG,
  USER_SEGMENTS,
  CONVERSION_GOALS,
  ECOMMERCE_CONFIG
} from '../config/analytics';

// Basic DataLayer Push
export const pushToDataLayer = (data) => {
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push(data);
  }
};

// Enhanced Event Tracking
export const trackEvent = ({
  event = ANALYTICS_EVENTS.BUTTON_CLICK,
  category = ANALYTICS_CATEGORIES.ENGAGEMENT,
  label,
  value,
  customDimensions = {},
  ...customParams
}) => {
  pushToDataLayer({
    event,
    category,
    label,
    value,
    ...customDimensions,
    ...customParams,
    timestamp: new Date().toISOString()
  });
};

// Page View Tracking
export const trackPageView = (pageName, pageSection, customDimensions = {}) => {
  pushToDataLayer({
    event: ANALYTICS_EVENTS.PAGE_VIEW,
    pageName,
    pageSection,
    ...customDimensions,
    timestamp: new Date().toISOString()
  });
};

// E-commerce Tracking
export const trackEcommerceEvent = ({
  event,
  products,
  currency = ECOMMERCE_CONFIG.CURRENCY,
  value,
  transactionId,
  ...customParams
}) => {
  pushToDataLayer({
    event,
    ecommerce: {
      currency,
      value,
      transaction_id: transactionId,
      items: products,
      ...customParams
    }
  });
};

// Purchase Tracking
export const trackPurchase = ({
  transactionId,
  products,
  value,
  currency = ECOMMERCE_CONFIG.CURRENCY,
  paymentMethod,
  discountCode
}) => {
  trackEcommerceEvent({
    event: ANALYTICS_EVENTS.PURCHASE,
    products,
    currency,
    value,
    transactionId,
    payment_method: paymentMethod,
    discount_code: discountCode
  });
};

// User Segment Tracking
export const trackUserSegment = (segment, userData = {}) => {
  pushToDataLayer({
    event: 'user_segment_update',
    user_segment: segment,
    ...userData
  });
};

// A/B Test Tracking
export const trackABTest = (testId, variant, userAction = null) => {
  pushToDataLayer({
    event: 'ab_test_exposure',
    test_id: testId,
    variant,
    user_action: userAction
  });
};

// Conversion Tracking
export const trackConversion = (goalType, goalName, value = null) => {
  pushToDataLayer({
    event: 'conversion',
    conversion_type: goalType,
    conversion_name: goalName,
    conversion_value: value,
    timestamp: new Date().toISOString()
  });
};

// Program Interaction Tracking
export const trackProgramInteraction = (programName, interactionType, details = {}) => {
  trackEvent({
    event: interactionType,
    category: ANALYTICS_CATEGORIES.PROGRAM,
    label: programName,
    ...details
  });
};

// User Journey Tracking
export const trackUserJourney = (stage, details = {}) => {
  pushToDataLayer({
    event: 'user_journey_update',
    journey_stage: stage,
    ...details,
    timestamp: new Date().toISOString()
  });
};

// Video Tracking
export const trackVideoEvent = (videoName, action, progress = null) => {
  trackEvent({
    event: `video_${action}`,
    category: ANALYTICS_CATEGORIES.CONTENT,
    label: videoName,
    video_progress: progress
  });
};

// Form Interaction Tracking
export const trackFormInteraction = (formName, action, details = {}) => {
  trackEvent({
    event: `form_${action}`,
    category: ANALYTICS_CATEGORIES.ENGAGEMENT,
    label: formName,
    ...details
  });
};

// Error Tracking
export const trackError = (errorType, errorMessage, errorDetails = {}) => {
  pushToDataLayer({
    event: 'error_occurred',
    error_type: errorType,
    error_message: errorMessage,
    ...errorDetails,
    timestamp: new Date().toISOString()
  });
};

// Performance Tracking
export const trackPerformance = (metric, value, details = {}) => {
  pushToDataLayer({
    event: 'performance_metric',
    metric_name: metric,
    metric_value: value,
    ...details,
    timestamp: new Date().toISOString()
  });
};